import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { toast } from 'react-toastify';
import { TRPCMethodEnum, TRPCResourceEnum } from '../../../api/trpcApi/types';
import { Title } from '../../../components/Title';
import { DeleteAssetButton } from '../../../components/buttons';
import { Chip } from '../../../components/chips';
import {
  ConfirmDeleteModalComponent,
  LoadingModalComponent
} from '../../../components/modals';
import { SearchBar } from '../../../components/search-bar';
import { RecordTable } from '../../../components/tables';
import { useLocalStorage, useTRPCRequest } from '../../../hooks';
import { TableRecord } from '../../../types/record-table';
import { Partner } from '../types';

export function PartnersList() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useLocalStorage<string>(
    window.location.pathname,
    ''
  );

  const [partners, setPartners] = useState<Partner[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { handleTRPCRequest } = useTRPCRequest();
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const [partnerToDelete, setPartnerToDelete] = useState<Partner>({
    partner_id: '',
    name: '',
    system_name: '',
    refersion_id: '',
    active: false
  });

  const fetchPartners = useCallback(
    async (search?: string) => {
      setIsLoading(true);
      const trpcRequest = {
        method: TRPCMethodEnum.list,
        resourceType: TRPCResourceEnum.partners,
        requestBody: {}
      };
      if (search) {
        trpcRequest.requestBody = { keyword: search };
      }
      const { res: partners, error } = await handleTRPCRequest(trpcRequest);

      if (!error && partners) {
        setPartners(partners);
      }
      setIsLoading(false);
    },
    [handleTRPCRequest]
  );

  useEffect(() => {
    fetchPartners(searchTerm ?? undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleAddNew = useCallback(() => {
    navigate('/partners/new');
  }, [navigate]);

  const handleDeletePartner = useCallback(
    (partner: TableRecord) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      event.preventDefault();
      setPartnerToDelete(partner as Partner);
      setConfirmDelete(true);
    },
    [setPartnerToDelete, setConfirmDelete]
  );

  const handleConfirmedDeletePartner = useCallback(async () => {
    setConfirmDelete(false);
    const { res, error } = await handleTRPCRequest({
      method: TRPCMethodEnum.delete,
      resourceType: TRPCResourceEnum.partners,
      requestBody: { partner_id: partnerToDelete.partner_id }
    });
    if (res && !error) {
      toast.success('Partner deleted successfully!');
      const filteredPartners = partners.filter(
        (partner: Partner) => partner.partner_id !== partnerToDelete.partner_id
      );
      setPartners(filteredPartners);
    }
  }, [handleTRPCRequest, partnerToDelete.partner_id, partners]);

  if (isLoading) {
    return <LoadingModalComponent isOpen={isLoading} />;
  }
  const noResults = partners?.length === 0;
  const noSearch = searchTerm === '';

  return (
    <>
      <Title title="Partners Search" />
      <SearchBar
        searchTerm={searchTerm}
        onSearch={search => setSearchTerm(search)}
        onAddNew={handleAddNew}
        classNames={['pb-2']}
      />

      {noResults && noSearch ? (
        <>
          <h2>No partners exist yet.</h2>
          <p> Click "+ Add New" to create your first partner!</p>
        </>
      ) : noResults ? (
        <h2>No partners found for "{searchTerm}".</h2>
      ) : (
        <RecordTable
          data={partners}
          idKey="partner_id"
          rowClassNames={[
            'card',
            'mb-[6px]',
            'flex',
            'h-[54px]',
            'cursor-pointer',
            'items-center',
            'align-middle'
          ]}
          calculateRowHref={partner => `/partners/${partner.partner_id}`}
          columns={[
            {
              columnKey: 'name',
              header: 'Name',
              onRender: (partner: TableRecord) => partner.name,
              widthClassNames: ['w-[18%]', 'ml-[15px]']
            },
            {
              columnKey: 'system_name',
              header: 'System Name',
              onRender: (partner: TableRecord) => partner.system_name,
              widthClassNames: ['w-[18%]']
            },
            {
              columnKey: 'refersion_id',
              header: 'Refersion ID',
              onRender: (partner: TableRecord) => partner.refersion_id,
              widthClassNames: ['w-[15%]']
            },
            {
              columnKey: 'active',
              header: 'Status',
              onRender: (partner: TableRecord) => (
                <Chip
                  backgroundColor={
                    partner.active ? 'bg-aqua' : 'bg-lighter-gray'
                  }
                >
                  {partner.active ? 'Active' : 'Inactive'}
                </Chip>
              ),
              widthClassNames: ['w-[10%]']
            },
            {
              columnKey: 'product_count',
              header: '# Products',
              onRender: (partner: TableRecord) => partner.product_count ?? 0,
              widthClassNames: ['w-[10%]']
            },
            {
              columnKey: 'created',
              header: 'Created',
              onRender: (partner: TableRecord) =>
                partner.created
                  ? new Date(partner.created).toLocaleDateString()
                  : '-',
              widthClassNames: ['w-[12%]']
            },
            {
              columnKey: 'actions',
              widthClassNames: ['ml-auto'],
              onRender: (partner: TableRecord) => {
                return (
                  <div className="flex gap-3 pr-3">
                    <DeleteAssetButton
                      onDeleteAsset={handleDeletePartner(partner)}
                    />
                  </div>
                );
              }
            }
          ]}
        />
      )}
      <ConfirmDeleteModalComponent
        isOpen={confirmDelete}
        entityName={partnerToDelete.name}
        onConfirm={handleConfirmedDeletePartner}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
}
